import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { InView } from 'react-intersection-observer'
import clsx from 'clsx'
import { AuthStatus } from '@hotmart/space-lib/dist/enum'
import { hasTextOverflow } from 'src/components/postCard/postCard.utils'
import { useMentionUtils } from 'src/components/postCommentInput/hooks'
import i18n from 'src/i18n'
import { NewsPostType, PostStatus, PostType } from 'src/hook/usePosts'
import { UserProfileUtils, useTrackEvents } from 'src/context/userProfileContext'
import { ComponentName } from 'src/utils/datahubDefaultEvent'
import useSendDataHubConsume from 'src/hook/useSendDatahubConsume/useSendDatahubConsume'
import {
  Actions,
  ArrivedFrom,
  ComponentNames,
  Entities,
  EventNames,
  ScreenNames
} from 'src/utils/datahub/enums'
import { debounce } from 'lodash'
import { ACCESS_TO_POST } from 'src/utils/constants'
import { readPost } from 'src/client/publication'
import { replaceNewlinesWithBr } from 'src/utils/functions'
import { useUser } from 'src/hook/useUser'
import { PostDescription } from 'src/components'
import { useUserProfile } from 'src/components/globalUserProfile'
import { FeedCardMedia } from '../feedCardMedia'
import { PostTitle, ViewMore } from './feedCardBody.styles'
import { FeedCardBodyProps } from './feedCardBody.types'

const FeedCardBody = ({ post, postPath, isProcessingStatus }: FeedCardBodyProps) => {
  const { authStatus } = useUser()
  const [viewMoreDescription, setViewMoreDescription] = useState(true)
  const [showViewMoreTextAction, setShowViewMoreTextAction] = useState(false)
  const postDescriptionRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { convertMentionToHTML } = useMentionUtils()
  const { loggedUserUcode, onOpen: onOpenUserProfile } = useUserProfile()
  const { sendOpenProfileEvent } = useTrackEvents({ loggedUserUcode })
  const { sendDataHubConsume } = useSendDataHubConsume()

  const mediaProps = isProcessingStatus ? { ...post, postType: PostType.TEXT } : post
  const showPostMediaContainer = [
    PostType.IMAGE,
    PostType.URL,
    PostType.VIDEO,
    PostType.AUDIO,
    NewsPostType.LIVE
  ].includes(post.postType)

  const postDescription = replaceNewlinesWithBr(post?.richDescription ?? post?.description ?? '')
  const description = convertMentionToHTML({
    text: postDescription,
    mentions: post?.mentions?.map((mention) => ({
      tag: mention?.tag,
      failed: Boolean(mention?.failed),
      failureReason: mention?.failureReason,
      ucode: mention?.metadata?.ucode,
      name: mention?.metadata?.name
    }))
  })

  const sendEventNewFeed = (eventName: EventNames) => {
    sendDataHubConsume({
      action: Actions.VIEW,
      entity: Entities.COMMUNITY,
      event: {
        eventName,
        screenName: ScreenNames.COMMUNITY_NEW_FEED,
        postId: post.id,
        postType: post.postType,
        communityId: Number(post?.ownerCollectionId),
        arrivedFrom: ArrivedFrom.COMMUNITY_FEED_V3
      }
    })
  }

  const handleViewPostPreviewDebounced = useCallback(
    debounce((inView: boolean) => {
      const shouldDispatchEvents =
        authStatus === AuthStatus.AUTHENTICATED && post.postStatus === PostStatus.ACTIVE && inView

      if (shouldDispatchEvents) {
        sendEventNewFeed(EventNames.VIEW_POST_PREVIEW)
        readPost(Number(post.id), ACCESS_TO_POST.COMMUNITY_POST_FEED)
      }
    }, 2000),
    []
  )

  const handleViewFullPostDescription = (event: React.MouseEvent) => {
    event.preventDefault()
    sendEventNewFeed(EventNames.CLICK_TO_SEE_MORE_POST_DETAIL)
    setViewMoreDescription(!viewMoreDescription)
  }

  const handleOpenPost = (event: React.MouseEvent | React.KeyboardEvent) => {
    const target = event.target as HTMLElement
    const isKeyboardEvent = typeof event === 'object' && 'key' in event
    const shouldNavigateToPost =
      (isKeyboardEvent && event.key === 'Enter' && target.tagName !== 'A') ||
      (!isKeyboardEvent && target.tagName !== 'A')

    if (shouldNavigateToPost) {
      sendEventNewFeed(EventNames.CLICK_TO_GO_TO_POST_DETAIL)
      navigate(postPath)
    }
  }

  useEffect(() => {
    setShowViewMoreTextAction(viewMoreDescription && hasTextOverflow(postDescriptionRef.current))
  }, [post, viewMoreDescription, postDescriptionRef])

  useEffect(() => {
    const elementsToOpenProfile = postDescriptionRef.current?.querySelectorAll(
      '[data-action="open-profile"]'
    )

    const callback = ({ ucode }) => {
      onOpenUserProfile(ucode)
      sendOpenProfileEvent({
        componentName: ComponentName.POST_FEED,
        userUcode: ucode,
        accessFromMention: true
      })
    }

    const removeListener = UserProfileUtils.openEventListener({
      elements: elementsToOpenProfile,
      componentName: ComponentName.POST_FEED,
      callback
    })

    return removeListener
  }, [])

  return (
    <>
      <InView as="div" onChange={handleViewPostPreviewDebounced} />

      <div className="_pl-4 _pr-4 _pb-4">
        <div
          className={clsx({
            '_mb-4': showPostMediaContainer
          })}
        >
          <div
            title={post.title}
            className="_cursor-pointer"
            role="button"
            tabIndex={0}
            onKeyDown={handleOpenPost}
            onClick={handleOpenPost}
          >
            <PostTitle
              title={post.title}
              className="_text-2 _text-gray-900 _font-weight-bold _mb-1"
            >
              {post.title}
            </PostTitle>

            {postDescription && (
              <PostDescription
                ref={postDescriptionRef}
                className="_font-14 _text-gray-900 _mb-1"
                limitChars={viewMoreDescription}
                description={description}
              />
            )}
          </div>

          {showViewMoreTextAction && (
            <ViewMore
              href="#"
              className="_font-14 _text-blue-light"
              title={i18n.t('feed.view_more')}
              onClick={handleViewFullPostDescription}
            >
              {i18n.t('feed.view_more')}
            </ViewMore>
          )}
        </div>

        {showPostMediaContainer && (
          <FeedCardMedia
            post={mediaProps}
            arrivedFrom={ComponentNames.POST_FEED}
            audioComponentName={ComponentName.PLAY_ON_FEED}
          />
        )}
      </div>
    </>
  )
}

export default FeedCardBody
